import { computed, watch } from 'vue';
import useLatchkeyStore from '~/stores/useLatchkeyStore';
import { useAvailability } from '@nuxt-common/media-card-large/app/composables/useAvailability';
import { useTraits } from '@nuxt-common/media-card-large/app/composables/useTraits';
import { useInitColor } from '@nuxt-common/media-card-large/app/modules/color';
import { useProductType } from '~/composables/useProductType';
import { toTraitId } from '../utils/utils';
import type { ParsedColors, ColorProps, Color, BaseColor, SelectedColorId } from '~/declaration/traits';
import type { ParsedLatchkeys } from '../declaration/latchkey';

function getFirstColor(colorData: ParsedColors | null): Color | null {
  return colorData?.colorArray[0] || null;
}

function toBaseColor(color: Color, availability: Record<string, boolean>): BaseColor {
  return {
    id: color.id,
    name: color.name,
    spriteOffset: color.swatchImage.swatchSpriteOffset,
    spriteIndex: color.swatchImage.swatchSpriteUrlIndex,
    available: availability[color.id],
  };
}

function useInitEachColor(id: string, latchkeyNames: LatchkeyName[], latchkeyColorMap: Record<string, ParsedColors>) {
  latchkeyNames.forEach((latchkeyName) => {
    const colors = latchkeyColorMap[latchkeyName];
    useInitColor({ id: toTraitId(id, latchkeyName), data: colors });
  });
}

function getPriceRange(priceToColors) {
  const firstPrice = priceToColors?.[0]?.price;
  const lastPriceValue = priceToColors?.[priceToColors?.length - 1];
  const lastPrice = lastPriceValue?.price;

  return `${lastPrice}-${firstPrice}`;
}

export default function useLatchkeyColor(
  id: string,
  selectedLatchkey: Ref<string>,
  { latchkeyNames, latchkeyColorMap }: ParsedLatchkeys,
): {
  selectedColorId: SelectedColorId;
  selectedColor: WritableComputedRef<Color | null>;
  colorProps: ComputedRef<ColorProps>;
  colors: ComputedRef<Color[]>;
} {
  const latchkeyStore = useLatchkeyStore();
  const { isConsolidatedLatchKey } = useProductType();

  if (isConsolidatedLatchKey) {
    useInitEachColor(id, latchkeyNames, latchkeyColorMap);

    const traitId = computed(() => toTraitId(id, selectedLatchkey.value));
    const previousTraitId = computed(() => toTraitId(id, latchkeyStore.previousSelectedLatchkey));

    const { selectedColor, selectedColorId } = useTraits(traitId);
    const { selectedColor: previousSelectedColor } = useTraits(previousTraitId);

    watch(selectedLatchkey, (newLatchkey) => {
      const colorData = latchkeyColorMap[newLatchkey];
      const newSelectedColor = colorData?.colorArray.find((color) => color.name === previousSelectedColor.value?.name) || getFirstColor(colorData);
      selectedColorId.value = newSelectedColor?.id || null;
    });

    const availability = computed(() => useAvailability(traitId.value).color.value);

    const colors = computed(() => {
      const colorData = latchkeyColorMap[selectedLatchkey.value || ''] || { colorArray: [] };
      return colorData.colorArray;
    });

    const colorProps = computed(() => {
      const colorData = latchkeyColorMap[selectedLatchkey.value || ''] || {
        colorArray: [],
        spriteUrls: [],
        priceToColors: [],
        priceToColorsExp: [],
      };

      const priceToColorsExp = [
        {
          colorIds: colorData?.priceToColors?.flatMap((priceTier) => priceTier.colorIds) ?? [],
          onSale: colorData?.priceToColors?.every((priceTier) => priceTier.onSale) ?? false,
          priceRange: (colorData?.priceToColors?.length > 1 && getPriceRange(colorData?.priceToColors)) ?? '',
        },
      ];

      return {
        colors: colorData.colorArray.map((color) => toBaseColor(color, availability.value)),
        spriteUrls: colorData.spriteUrls,
        priceToColors: colorData.priceToColors,
        priceToColorsExp,
      };
    });

    return {
      colors,
      selectedColor,
      selectedColorId,
      colorProps,
    };
  }

  return {
    colors: computed(() => []),
    selectedColor: computed(() => null),
    selectedColorId: computed(() => null),
    colorProps: computed(() => ({
      colors: [],
      spriteUrls: [],
      priceToColors: [],
      priceToColorsExp: [],
    })),
  };
}
